import { Button, Tooltip, palette } from "@minervaproject/fds";
import { memo, useContext } from "react";
import { useDrag } from "react-dnd";

import { ForumAPIContext } from "contexts/forum_api_context";
import { useCapabilities } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { collect, useDragHandler } from "hooks/use_drag_handler";
import { featureContentInOneUp } from "shared/reducers/classroom";
import { PaneContentType, ResourceType, ResourceTypeType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { getGoogleDriveUrl, getResourceIcon, needsSignatureToAccess } from "utils/resource";

import "./resource_button.scss";

const ResourceButton = memo(function ResourceButton({ resource }: { resource: ResourceType }) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const { canControlClassroom, canParticipateInClass } = useCapabilities();
  const isResourceClickableForStudents = ![
    ResourceTypeType.HtmlSlide,
    ResourceTypeType.Whiteboard,
  ].includes(resource.type);
  const studentsCanDraw = useAppSelector(classroomSelectors.selectStudentsCanDraw());
  const readOnly = canParticipateInClass && !studentsCanDraw;
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());

  const [{ isDragging }, drag] = useDrag({
    type: PaneContentType.Resource,
    item: { id: resource.id },
    collect,
  });
  useDragHandler(isDragging);

  const handleClickResource = (resourceId: string) => async () => {
    if (canControlClassroom || activeBreakoutSessionId) {
      dispatch(
        featureContentInOneUp({
          contentId: resourceId,
          contentType: PaneContentType.Resource,
          breakoutRoomId: breakoutRoom?.id,
          ownerId: currentUserId,
        }),
      );
    } else if (isResourceClickableForStudents) {
      let url;
      if (resource.type === ResourceTypeType.ForumDoc) {
        url = await forumAPI.getForumDocSignedUrl(resource.id, { readOnly });
      } else if (resource.type === ResourceTypeType.ForumWorkbook) {
        url = await forumAPI.getForumWorkbookSignedUrl(resource.id, { readOnly });
      } else if (resource.type === ResourceTypeType.GoogleDrive) {
        url = getGoogleDriveUrl(resource);
      } else if (needsSignatureToAccess(resource.type, resource.url)) {
        url = await forumAPI.getSignedUrl(resource.id, { contentDisposition: "inline" });
      } else if (resource.type === ResourceTypeType.Screenshare) {
        return;
      } else {
        url = resource.url;
      }
      window.open(url, "_blank");
      window.focus();
    }
  };

  const ResourceIcon = getResourceIcon(resource);

  const contents = (
    <Button
      className="mb1 flex-none"
      kind="classroom-secondary"
      onClick={handleClickResource(resource.id)}
      ref={canControlClassroom || activeBreakoutSessionId ? drag : null}
      disabled={!canControlClassroom && !activeBreakoutSessionId && !isResourceClickableForStudents}
    >
      <ResourceIcon color="white" />
      <span className="truncate">{resource.title}</span>
    </Button>
  );

  return (
    <Tooltip backgroundColor={palette.blackTint20} iconComponent={contents} place="left">
      <div style={{ maxWidth: "200px", overflowWrap: "break-word" }}>{resource.title}</div>
    </Tooltip>
  );
});

export { ResourceButton };
